import { Component, Input, computed } from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { SpinnerVariant, SpinnerSize, SpinnerType } from '../models/spinner.model';

const spinnerVariants = cva({
  base: ['inline-block transition-all duration-300 ease-out'].join(' '),
  variants: {
    size: {
      xs: 'w-3 h-3',
      sm: 'w-4 h-4',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
      xl: 'w-12 h-12',
      '2xl': 'w-16 h-16',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const circularVariants = cva({
  base: ['rounded-full border-2 border-solid animate-spin', 'backdrop-blur-sm'].join(' '),
  variants: {
    variant: {
      default: 'border-gray-300 border-t-gray-900 dark:border-gray-600 dark:border-t-white',
      primary: 'border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400',
      secondary: 'border-gray-200 border-t-gray-600 dark:border-gray-700 dark:border-t-gray-300',
      success: 'border-cyan-200 border-t-green-600 dark:border-green-800 dark:border-t-green-400',
      warning: 'border-yellow-200 border-t-yellow-600 dark:border-yellow-800 dark:border-t-yellow-400',
      error: 'border-red-200 border-t-red-600 dark:border-red-800 dark:border-t-red-400',
      premium: 'border-gradient-to-r from-primary-200 via-primary-400 to-primary-600 border-t-primary-600',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const dotsVariants = cva({
  base: ['flex items-center space-x-1'].join(' '),
  variants: {
    variant: {
      default: '[&>div]:bg-gray-600 dark:[&>div]:bg-gray-300',
      primary: '[&>div]:bg-primary-600 dark:[&>div]:bg-primary-400',
      secondary: '[&>div]:bg-gray-600 dark:[&>div]:bg-gray-300',
      success: '[&>div]:bg-cyan-600 dark:[&>div]:bg-cyan-400',
      warning: '[&>div]:bg-yellow-600 dark:[&>div]:bg-yellow-400',
      error: '[&>div]:bg-red-600 dark:[&>div]:bg-red-400',
      premium: '[&>div]:bg-linear-to-r [&>div]:from-primary-400 [&>div]:to-primary-600',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

@Component({
  selector: 'ui-spinner-glass',
  standalone: true,
  imports: [],
  templateUrl: './spinner-glass.component.html',
  styleUrls: ['./spinner-glass.component.scss'],
})
export class SpinnerGlassComponent {
  @Input() type: SpinnerType = 'circular';
  @Input() variant: SpinnerVariant = 'default';
  @Input() size: SpinnerSize = 'md';
  @Input() label: string = '';
  @Input() showLabel: boolean = false;
  @Input() themeSection: ThemeSection = 'global';

  protected computedSpinnerClasses = computed(() => {
    return spinnerVariants({
      size: this.size,
    });
  });

  protected computedCircularClasses = computed(() => {
    return circularVariants({
      variant: this.variant,
    });
  });

  protected computedDotsClasses = computed(() => {
    return dotsVariants({
      variant: this.variant,
    });
  });

  protected pulseClasses = computed(() => {
    const baseClasses = 'w-full h-full';
    const variantClasses = {
      default: 'bg-gray-600 dark:bg-gray-300',
      primary: 'bg-primary-600 dark:bg-primary-400',
      secondary: 'bg-gray-600 dark:bg-gray-300',
      success: 'bg-cyan-600 dark:bg-cyan-400',
      warning: 'bg-yellow-600 dark:bg-yellow-400',
      error: 'bg-red-600 dark:bg-red-400',
      premium: 'bg-linear-to-r from-primary-400 to-primary-600',
    };

    return `${baseClasses} ${variantClasses[this.variant]}`;
  });

  protected barClasses = computed(() => {
    const sizeClasses = {
      xs: 'w-0.5 h-3',
      sm: 'w-0.5 h-4',
      md: 'w-1 h-6',
      lg: 'w-1 h-8',
      xl: 'w-1.5 h-12',
      '2xl': 'w-2 h-16',
    };

    const variantClasses = {
      default: 'bg-gray-600 dark:bg-gray-300',
      primary: 'bg-primary-600 dark:bg-primary-400',
      secondary: 'bg-gray-600 dark:bg-gray-300',
      success: 'bg-cyan-600 dark:bg-cyan-400',
      warning: 'bg-yellow-600 dark:bg-yellow-400',
      error: 'bg-red-600 dark:bg-red-400',
      premium: 'bg-linear-to-t from-primary-400 to-primary-600',
    };

    return `${sizeClasses[this.size]} ${variantClasses[this.variant]}`;
  });

  protected ringOuterClasses = computed(() => {
    const baseClasses = 'w-full h-full border-2 border-solid';
    const variantClasses = {
      default: 'border-gray-300 border-t-gray-900 dark:border-gray-600 dark:border-t-white',
      primary: 'border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400',
      secondary: 'border-gray-200 border-t-gray-600 dark:border-gray-700 dark:border-t-gray-300',
      success: 'border-cyan-200 border-t-green-600 dark:border-green-800 dark:border-t-green-400',
      warning: 'border-yellow-200 border-t-yellow-600 dark:border-yellow-800 dark:border-t-yellow-400',
      error: 'border-red-200 border-t-red-600 dark:border-red-800 dark:border-t-red-400',
      premium: 'border-primary-200 border-t-primary-600',
    };

    return `${baseClasses} ${variantClasses[this.variant]}`;
  });

  protected ringInnerClasses = computed(() => {
    const baseClasses = 'w-full h-full border border-solid';
    const variantClasses = {
      default: 'border-gray-900 border-b-gray-300 dark:border-white dark:border-b-gray-600',
      primary: 'border-primary-600 border-b-primary-200 dark:border-primary-400 dark:border-b-primary-800',
      secondary: 'border-gray-600 border-b-gray-200 dark:border-gray-300 dark:border-b-gray-700',
      success: 'border-cyan-600 border-b-green-200 dark:border-cyan-400 dark:border-b-green-800',
      warning: 'border-yellow-600 border-b-yellow-200 dark:border-yellow-400 dark:border-b-yellow-800',
      error: 'border-red-600 border-b-red-200 dark:border-red-400 dark:border-b-red-800',
      premium: 'border-primary-600 border-b-primary-200',
    };

    return `${baseClasses} ${variantClasses[this.variant]}`;
  });

  protected orbitCenterClasses = computed(() => {
    const sizeClasses = {
      xs: 'w-1 h-1',
      sm: 'w-1 h-1',
      md: 'w-2 h-2',
      lg: 'w-2 h-2',
      xl: 'w-3 h-3',
      '2xl': 'w-4 h-4',
    };

    const variantClasses = {
      default: 'bg-gray-600 dark:bg-gray-300',
      primary: 'bg-primary-600 dark:bg-primary-400',
      secondary: 'bg-gray-600 dark:bg-gray-300',
      success: 'bg-cyan-600 dark:bg-cyan-400',
      warning: 'bg-yellow-600 dark:bg-yellow-400',
      error: 'bg-red-600 dark:bg-red-400',
      premium: 'bg-linear-to-r from-primary-400 to-primary-600',
    };

    return `${sizeClasses[this.size]} ${variantClasses[this.variant]}`;
  });

  protected orbitRingClasses = computed(() => {
    return 'w-full h-full border border-dashed border-gray-300 dark:border-gray-600';
  });

  protected orbitDotClasses = computed(() => {
    const sizeClasses = {
      xs: 'w-1 h-1',
      sm: 'w-1 h-1',
      md: 'w-1.5 h-1.5',
      lg: 'w-2 h-2',
      xl: 'w-2.5 h-2.5',
      '2xl': 'w-3 h-3',
    };

    const variantClasses = {
      default: 'bg-gray-600 dark:bg-gray-300',
      primary: 'bg-primary-600 dark:bg-primary-400',
      secondary: 'bg-gray-600 dark:bg-gray-300',
      success: 'bg-cyan-600 dark:bg-cyan-400',
      warning: 'bg-yellow-600 dark:bg-yellow-400',
      error: 'bg-red-600 dark:bg-red-400',
      premium: 'bg-linear-to-r from-primary-400 to-primary-600',
    };

    return `${sizeClasses[this.size]} ${variantClasses[this.variant]}`;
  });
}

/**
 * Usage Examples:
 *
 * Basic circular spinner:
 * <ui-spinner-glass></ui-spinner-glass>
 *
 * Different types:
 * <ui-spinner-glass type="circular"></ui-spinner-glass>
 * <ui-spinner-glass type="dots"></ui-spinner-glass>
 * <ui-spinner-glass type="pulse"></ui-spinner-glass>
 * <ui-spinner-glass type="bars"></ui-spinner-glass>
 * <ui-spinner-glass type="ring"></ui-spinner-glass>
 * <ui-spinner-glass type="orbit"></ui-spinner-glass>
 *
 * Different variants:
 * <ui-spinner-glass variant="primary"></ui-spinner-glass>
 * <ui-spinner-glass variant="success"></ui-spinner-glass>
 * <ui-spinner-glass variant="error"></ui-spinner-glass>
 * <ui-spinner-glass variant="premium"></ui-spinner-glass>
 *
 * Different sizes:
 * <ui-spinner-glass size="xs"></ui-spinner-glass>
 * <ui-spinner-glass size="sm"></ui-spinner-glass>
 * <ui-spinner-glass size="md"></ui-spinner-glass>
 * <ui-spinner-glass size="lg"></ui-spinner-glass>
 * <ui-spinner-glass size="xl"></ui-spinner-glass>
 * <ui-spinner-glass size="2xl"></ui-spinner-glass>
 *
 * With label:
 * <ui-spinner-glass
 *   label="Loading..."
 *   [showLabel]="true"
 *   variant="primary"
 * ></ui-spinner-glass>
 *
 * E-commerce examples:
 *
 * Loading cart:
 * <ui-spinner-glass
 *   type="circular"
 *   variant="primary"
 *   size="md"
 *   label="Adding to cart..."
 *   [showLabel]="true"
 * ></ui-spinner-glass>
 *
 * Processing payment:
 * <ui-spinner-glass
 *   type="ring"
 *   variant="success"
 *   size="lg"
 *   label="Processing payment..."
 *   [showLabel]="true"
 * ></ui-spinner-glass>
 *
 * Loading products:
 * <ui-spinner-glass
 *   type="dots"
 *   variant="premium"
 *   size="md"
 * ></ui-spinner-glass>
 *
 * Page loading overlay:
 * <div class="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
 *   <ui-spinner-glass
 *     type="orbit"
 *     variant="primary"
 *     size="2xl"
 *     label="Loading store..."
 *     [showLabel]="true"
 *   ></ui-spinner-glass>
 * </div>
 *
 * Button loading state:
 * <ui-button-glass [disabled]="isLoading">
 *   @if (isLoading) {
 *     <ui-spinner-glass type="circular" variant="primary" size="sm"></ui-spinner-glass>
 *     <span class="ml-2">Loading...</span>
 *   } @else {
 *     Add to Cart
 *   }
 * </button>
 */
